import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { backupConfigAPI } from '../api/backupConfig';
import { installationAPI } from '../api/installation';
import { getAllScheduleMasters, getAllSftpServerMasters } from '../services/api';
import { toast } from 'react-toastify';
import './CreateBackupConfigTabs.css';

const CreateBackupConfigStandalone = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('installation');
  const [loading, setLoading] = useState(false);
  const [installations, setInstallations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [sftpServers, setSftpServers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [usePresetSchedule, setUsePresetSchedule] = useState(false);
  const [presetSchedule, setPresetSchedule] = useState('daily-1x');

  // New state for schedule-SFTP mapping
  const [scheduleSftpMapping, setScheduleSftpMapping] = useState({});

  const [formData, setFormData] = useState({
    installation: '',
    configName: '',
    description: '',
    sqlServerConfig: {
      host: '',
      port: 1433,
      username: '',
      password: '',
      database: '',
      instanceName: ''
    },
    backupPath: '',
    schedules: [],
    sftpServers: [],
    isActive: true
  });

  // Preset schedule options
  const presetSchedules = [
    { id: 'hourly', name: 'Every Hour', description: 'Backup every hour (24 times per day)', icon: '⏰' },
    { id: 'daily-1x', name: 'Daily 1 Time', description: 'Backup once daily at 2:00 AM', icon: '🌙' },
    { id: 'daily-2x', name: 'Daily 2 Times', description: 'Backup twice daily (2:00 AM & 2:00 PM)', icon: '☀️' },
    { id: 'daily-3x', name: 'Daily 3 Times', description: 'Backup 3 times daily (6:00 AM, 2:00 PM, 10:00 PM)', icon: '🔄' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-generate the fixed backup path from the configuration name.
  // Format: /var/www/backup/<config_name_lowercase_with_underscores>
  useEffect(() => {
    const sanitized = formData.configName.trim().toLowerCase().replace(/\s+/g, '_');
    const generatedPath = sanitized ? `/var/www/backup/${sanitized}` : '/var/www/backup/';
    setFormData(prev => (
      prev.backupPath === generatedPath ? prev : { ...prev, backupPath: generatedPath }
    ));
  }, [formData.configName]);

  const fetchData = async () => {
    try {
      const [installationsRes, schedulesRes, serversRes] = await Promise.all([
        installationAPI.getAll(1, 100),
        getAllScheduleMasters(),
        getAllSftpServerMasters()
      ]);

      setInstallations(installationsRes.data);
      setSchedules(schedulesRes.data.filter(s => s.isActive));
      setSftpServers(serversRes.data.filter(s => s.isActive));
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const handleChange = (e, section, field) => {
    const { value, type, checked } = e.target;

    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: type === 'number' ? Number(value) : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleScheduleToggle = (scheduleId) => {
    setFormData(prev => {
      const schedules = prev.schedules.includes(scheduleId)
        ? prev.schedules.filter(id => id !== scheduleId)
        : [...prev.schedules, scheduleId];
      return { ...prev, schedules };
    });

    // Initialize mapping for new schedule with all selected SFTP servers
    if (!formData.schedules.includes(scheduleId)) {
      setScheduleSftpMapping(prev => ({
        ...prev,
        [scheduleId]: formData.sftpServers
      }));
    } else {
      // Remove mapping when schedule is deselected
      setScheduleSftpMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[scheduleId];
        return newMapping;
      });
    }
  };

  const handleSftpToggle = (serverId) => {
    setFormData(prev => {
      const sftpServers = prev.sftpServers.includes(serverId)
        ? prev.sftpServers.filter(id => id !== serverId)
        : [...prev.sftpServers, serverId];
      return { ...prev, sftpServers };
    });
  };

  // New handler for mapping SFTP servers to specific schedules
  const handleMappingSftpToggle = (scheduleId, sftpId) => {
    setScheduleSftpMapping(prev => {
      const currentMapping = prev[scheduleId] || [];
      const newMapping = currentMapping.includes(sftpId)
        ? currentMapping.filter(id => id !== sftpId)
        : [...currentMapping, sftpId];

      return {
        ...prev,
        [scheduleId]: newMapping
      };
    });
  };

  const getSchedulesForPreset = () => {
    // Filter schedules based on preset selection
    switch (presetSchedule) {
      case 'hourly':
        return schedules.filter(s => s.frequency === 'hourly').map(s => s._id);
      case 'daily-1x':
        return schedules.filter(s => s.frequency === 'daily' && s.time === '02:00').slice(0, 1).map(s => s._id);
      case 'daily-2x':
        return schedules.filter(s => s.frequency === 'daily' && (s.time === '02:00' || s.time === '14:00')).slice(0, 2).map(s => s._id);
      case 'daily-3x':
        return schedules.filter(s => s.frequency === 'daily' && ['06:00', '14:00', '22:00'].includes(s.time)).slice(0, 3).map(s => s._id);
      default:
        return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.installation) {
      toast.error('Please select an installation');
      return;
    }

    // Use preset schedules if selected
    const finalSchedules = usePresetSchedule ? getSchedulesForPreset() : formData.schedules;

    if (finalSchedules.length === 0) {
      toast.error('Please select at least one schedule');
      return;
    }

    if (formData.sftpServers.length === 0) {
      toast.error('Please select at least one SFTP server');
      return;
    }

    // Validate mapping
    for (const scheduleId of finalSchedules) {
      if (!scheduleSftpMapping[scheduleId] || scheduleSftpMapping[scheduleId].length === 0) {
        toast.error('Please map at least one SFTP server to each schedule');
        return;
      }
    }

    setLoading(true);

    try {
      const selectedInstallation = installations.find(i => i._id === formData.installation);

      // Create multiple configurations based on schedule-SFTP mappings
      const configsToCreate = [];

      for (const scheduleId of finalSchedules) {
        const mappedSftpServers = scheduleSftpMapping[scheduleId] || [];
        const schedule = schedules.find(s => s._id === scheduleId);

        for (const sftpId of mappedSftpServers) {
          const sftpServer = sftpServers.find(s => s._id === sftpId);

          const configData = {
            configName: `${formData.configName} - ${schedule?.name || schedule?.time} - ${sftpServer?.name}`,
            description: formData.description,
            sqlServerConfig: formData.sqlServerConfig,
            backupPath: formData.backupPath,
            schedules: [scheduleId],
            sftpServers: [sftpId],
            isActive: formData.isActive
          };

          configsToCreate.push(configData);
        }
      }

      // Create all configurations
      const createPromises = configsToCreate.map(config => 
        backupConfigAPI.create(selectedInstallation.uniqueId, config)
      );

      await Promise.all(createPromises);

      toast.success(`Successfully created ${configsToCreate.length} backup configuration(s)`);
      navigate('/backup-configs');
    } catch (error) {
      toast.error(error.message || 'Failed to create backup configurations');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'installation', label: 'Installation', icon: '🏢' },
    { id: 'basic', label: 'Basic Info', icon: '📝' },
    { id: 'sql', label: 'Source Server', icon: '🗄️' },
    { id: 'schedule', label: 'Schedule', icon: '⏰' },
    { id: 'sftp', label: 'Destination Servers', icon: '🖥️' },
    { id: 'mapping', label: 'Mapping', icon: '🔗' }
  ];

  const isTabValid = (tabId) => {
    switch (tabId) {
      case 'installation':
        return formData.installation !== '';
      case 'basic':
        return formData.configName.length >= 3;
      case 'sql':
        return formData.sqlServerConfig.host && formData.sqlServerConfig.username &&
               formData.sqlServerConfig.password && formData.sqlServerConfig.database;
      case 'schedule':
        return usePresetSchedule ? true : formData.schedules.length > 0;
      case 'sftp':
        return formData.sftpServers.length > 0;
      case 'mapping':
        const finalSchedules = usePresetSchedule ? getSchedulesForPreset() : formData.schedules;
        return finalSchedules.every(scheduleId => 
          scheduleSftpMapping[scheduleId] && scheduleSftpMapping[scheduleId].length > 0
        );
      default:
        return false;
    }
  };

  const canProceedToNext = () => {
    return isTabValid(activeTab);
  };

  const nextTab = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1 && canProceedToNext()) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  };

  const prevTab = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  return (
    <div className="config-form-tabs-container">
      <div className="form-header">
        <h1>Create Backup Configuration</h1>
        <p>Configure a new backup configuration</p>
      </div>

      <div className="tabs-navigation">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${isTabValid(tab.id) ? 'valid' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            {isTabValid(tab.id) && <span className="tab-check">✓</span>}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="config-tabs-form">
        <div className="tab-content">
          {/* Installation Selection Tab */}
          {activeTab === 'installation' && (
            <div className="tab-pane">
              <h3>Select Installation</h3>

              {installations.length === 0 ? (
                <div className="empty-message">
                  <p>No installations available. Please create an installation first.</p>
                  <Link to="/installations/create" className="btn btn-primary">
                    Create Installation
                  </Link>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="installation">Installation *</label>
                    <select
                      id="installation"
                      name="installation"
                      value={formData.installation}
                      onChange={(e) => handleChange(e, null, 'installation')}
                      required
                    >
                      <option value="">-- Select Installation --</option>
                      {installations.map(inst => (
                        <option key={inst._id} value={inst._id}>
                          {inst.installationName} ({inst.computerName})
                        </option>
                      ))}
                    </select>
                    <small className="help-text">
                      Select the installation for which you want to create a backup configuration
                    </small>
                  </div>

                  {formData.installation && (
                    <div className="installation-info">
                      {(() => {
                        const selected = installations.find(i => i._id === formData.installation);
                        return selected ? (
                          <div className="info-card">
                            <h4>Selected Installation Details</h4>
                            <div className="info-grid">
                              <div className="info-item">
                                <span className="label">Installation Name:</span>
                                <span className="value">{selected.installationName}</span>
                              </div>
                              <div className="info-item">
                                <span className="label">Computer Name:</span>
                                <span className="value">{selected.computerName}</span>
                              </div>
                              {selected.description && (
                                <div className="info-item full-width">
                                  <span className="label">Description:</span>
                                  <span className="value">{selected.description}</span>
                                </div>
                              )}
                              <div className="info-item">
                                <span className="label">Unique ID:</span>
                                <span className="value mono">{selected.uniqueId}</span>
                              </div>
                              <div className="info-item">
                                <span className="label">Status:</span>
                                <span className={`badge ${selected.isActive ? 'badge-success' : 'badge-danger'}`}>
                                  {selected.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="tab-pane">
              <h3>Basic Information</h3>

              <div className="form-group">
                <label htmlFor="configName">Configuration Name *</label>
                <input
                  type="text"
                  id="configName"
                  name="configName"
                  value={formData.configName}
                  onChange={(e) => handleChange(e, null, 'configName')}
                  required
                  placeholder="e.g., Production DB Backup"
                />
                <small className="help-text">
                  Base name (schedule and destination details will be auto-appended)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={(e) => handleChange(e, null, 'description')}
                  rows="3"
                  placeholder="Optional description"
                />
              </div>

              <div className="form-group">
                <label htmlFor="backupPath">Custom Backup Path on Destination Server</label>
                <input
                  type="text"
                  id="backupPath"
                  name="backupPath"
                  value={formData.backupPath}
                  readOnly
                  placeholder="/var/www/backup/"
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
                <small className="help-text">
                  Auto-generated from the Configuration Name (lowercase, spaces replaced with underscores). Format: <code>/var/www/backup/&lt;config_name&gt;</code>
                </small>
              </div>
            </div>
          )}

          {/* Source Server Tab */}
          {activeTab === 'sql' && (
            <div className="tab-pane">
              <h3>Source Server Configuration</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="host">Host *</label>
                  <input
                    type="text"
                    id="host"
                    value={formData.sqlServerConfig.host}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'host')}
                    required
                    placeholder="localhost or IP address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="port">Port *</label>
                  <input
                    type="number"
                    id="port"
                    value={formData.sqlServerConfig.port}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'port')}
                    required
                    min="1"
                    max="65535"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    type="text"
                    id="username"
                    value={formData.sqlServerConfig.username}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'username')}
                    required
                    placeholder="Source server username"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <div className="password-input">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={formData.sqlServerConfig.password}
                      onChange={(e) => handleChange(e, 'sqlServerConfig', 'password')}
                      required
                      placeholder="Source server password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="database">Database *</label>
                  <input
                    type="text"
                    id="database"
                    value={formData.sqlServerConfig.database}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'database')}
                    required
                    placeholder="Database name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="instanceName">Instance Name</label>
                  <input
                    type="text"
                    id="instanceName"
                    value={formData.sqlServerConfig.instanceName}
                    onChange={(e) => handleChange(e, 'sqlServerConfig', 'instanceName')}
                    placeholder="Optional instance name"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="tab-pane">
              <h3>Backup Schedule</h3>

              {/* <div className="schedule-type-selector">
                <label className="radio-card">
                  <input
                    type="radio"
                    name="scheduleType"
                    checked={usePresetSchedule}
                    onChange={() => setUsePresetSchedule(true)}
                  />
                  <div className="radio-content">
                    <strong>Use Preset Schedule</strong>
                    <span>Quick setup with common backup schedules</span>
                  </div>
                </label>

                <label className="radio-card">
                  <input
                    type="radio"
                    name="scheduleType"
                    checked={!usePresetSchedule}
                    onChange={() => setUsePresetSchedule(false)}
                  />
                  <div className="radio-content">
                    <strong>Custom Schedule</strong>
                    <span>Select from available schedule masters</span>
                  </div>
                </label>
              </div> */}

              {usePresetSchedule ? (
                <div className="preset-schedules">
                  {presetSchedules.map(preset => (
                    <div
                      key={preset.id}
                      className={`preset-card ${presetSchedule === preset.id ? 'selected' : ''}`}
                      onClick={() => setPresetSchedule(preset.id)}
                    >
                      <div className="preset-icon">{preset.icon}</div>
                      <h4>{preset.name}</h4>
                      <p>{preset.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {schedules.length === 0 ? (
                    <div className="empty-message">
                      <p>No schedules available. Please create a schedule first.</p>
                      <button
                        type="button"
                        onClick={() => window.open('/schedules/new', '_blank')}
                        className="btn btn-primary"
                      >
                        Create Schedule
                      </button>
                    </div>
                  ) : (
                    <div className="selection-grid">
                      {schedules.map(schedule => (
                        <div
                          key={schedule._id}
                          className={`selection-card ${formData.schedules.includes(schedule._id) ? 'selected' : ''}`}
                          onClick={() => handleScheduleToggle(schedule._id)}
                        >
                          <input
                            type="checkbox"
                            checked={formData.schedules.includes(schedule._id)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="card-content">
                            <h4>{schedule.name}</h4>
                            <p className="frequency">{schedule.frequency} at {schedule.time}</p>
                            {schedule.description && <p className="description">{schedule.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* SFTP Tab */}
          {activeTab === 'sftp' && (
            <div className="tab-pane">
              <h3>Destination Servers</h3>

              {sftpServers.length === 0 ? (
                <div className="empty-message">
                  <p>No destination servers available. Please create an destination server first.</p>
                  <button
                    type="button"
                    onClick={() => window.open('/sftp-servers/new', '_blank')}
                    className="btn btn-primary"
                  >
                    Create Destination Server
                  </button>
                </div>
              ) : (
                <div className="selection-grid">
                  {sftpServers.map(server => (
                    <div
                      key={server._id}
                      className={`selection-card ${formData.sftpServers.includes(server._id) ? 'selected' : ''}`}
                      onClick={() => handleSftpToggle(server._id)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.sftpServers.includes(server._id)}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="card-content">
                        <h4>{server.name}</h4>
                        <p className="host">{server.host}:{server.port}</p>
                        {server.description && <p className="description">{server.description}</p>}
                        <p className="priority">Priority: {server.priority}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-group" style={{ marginTop: '2rem' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={(e) => handleChange(e, null, 'isActive')}
                  />
                  Active
                </label>
              </div>
            </div>
          )}

          {/* Mapping Tab */}
          {activeTab === 'mapping' && (
            <div className="tab-pane">
              <h3>Schedule-Destination Server Mapping</h3>
              <p className="help-text" style={{ marginBottom: '1.5rem' }}>
                Map each schedule to specific destination server(s). Each combination will create a separate backup configuration.
              </p>

              {(usePresetSchedule ? getSchedulesForPreset() : formData.schedules).length === 0 ? (
                <div className="empty-message">
                  <p>⚠️ Please select schedules in the previous step</p>
                </div>
              ) : (
                <>
                  {(usePresetSchedule ? getSchedulesForPreset() : formData.schedules).map(scheduleId => {
                    const schedule = schedules.find(s => s._id === scheduleId);
                    if (!schedule) return null;

                    return (
                      <div key={scheduleId} style={{ marginBottom: '2rem' }}>
                        <h4 style={{ marginBottom: '1rem', color: '#2563eb' }}>
                          📅 {schedule.name} ({schedule.frequency} {schedule.time ? `at ${schedule.time}` : ''})
                        </h4>

                        <div className="selection-grid">
                          {sftpServers
                            .filter(server => formData.sftpServers.includes(server._id))
                            .map(server => (
                              <div
                                key={server._id}
                                className={`selection-card ${
                                  scheduleSftpMapping[scheduleId]?.includes(server._id) ? 'selected' : ''
                                }`}
                                onClick={() => handleMappingSftpToggle(scheduleId, server._id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={scheduleSftpMapping[scheduleId]?.includes(server._id) || false}
                                  onChange={() => {}}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="card-content">
                                  <h4>{server.name}</h4>
                                  <p className="host">{server.host}:{server.port}</p>
                                  {server.description && <p className="description">{server.description}</p>}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })}

                  <div className="info-card" style={{ marginTop: '2rem' }}>
                    <h4>📊 Configuration Summary</h4>
                    <p>
                      <strong>Total Configurations to Create:</strong>{' '}
                      {Object.values(scheduleSftpMapping).reduce((sum, servers) => sum + servers.length, 0)}
                    </p>
                    <small className="help-text">
                      Each schedule-destination server combination will create a separate backup configuration with auto-generated names.
                    </small>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="tab-actions">
          <div className="left-actions">
            <button
              type="button"
              onClick={() => navigate('/backup-configs')}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
          </div>

          <div className="right-actions">
            {activeTab !== 'installation' && (
              <button
                type="button"
                onClick={prevTab}
                className="btn btn-secondary"
                disabled={loading}
              >
                ← Previous
              </button>
            )}

            {activeTab !== 'mapping' ? (
              <button
                type="button"
                onClick={nextTab}
                className="btn btn-primary"
                disabled={!canProceedToNext() || loading}
              >
                Next →
              </button>
            ) : (
              <button
                type="submit"
                className="btn btn-success"
                disabled={loading || !isTabValid('mapping') || installations.length === 0}
              >
                {loading ? 'Creating...' : 'Create Configuration(s)'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateBackupConfigStandalone;